import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DeviceLabels,
  useAudioInputs,
  useAudioOutputs,
  useMeetingManager,
  useVideoInputs
} from 'amazon-chime-sdk-component-library-react'
import { mapDevicesToOptions, extractDeviceId, formatDeviceError } from '@utils'
import type { DeviceSelectionState, UseDeviceSelectionOptions } from '@models/meeting-hooks'
import { useMeetingStore } from '@stores/meeting'

export function useDeviceSelection({ autoSelect }: UseDeviceSelectionOptions): DeviceSelectionState {
  const meetingManager = useMeetingManager()
  const audioInputsContext = useAudioInputs()
  const videoInputsContext = useVideoInputs()
  const audioOutputsContext = useAudioOutputs()
  const isActiveMeeting = useMeetingStore((state) => state.isActiveMeeting)

  const [audioError, setAudioError] = useState<string | undefined>(undefined)
  const [videoError, setVideoError] = useState<string | undefined>(undefined)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const assertMeetingReady = useCallback(() => {
    if (!isActiveMeeting() || !meetingManager.meetingSession) {
      throw new Error('Impossible de gérer les périphériques sans meeting actif.')
    }
  }, [isActiveMeeting, meetingManager])

  // Device lists
  const audioInputs = useMemo(
    () => mapDevicesToOptions({ devices: audioInputsContext.devices }),
    [audioInputsContext.devices]
  )
  const videoInputs = useMemo(
    () => mapDevicesToOptions({ devices: videoInputsContext.devices }),
    [videoInputsContext.devices]
  )
  const audioOutputs = useMemo(
    () => mapDevicesToOptions({ devices: audioOutputsContext.devices }),
    [audioOutputsContext.devices]
  )

  // Selected devices
  const selectedAudioInput = extractDeviceId({ device: audioInputsContext.selectedDevice }) ?? undefined
  const selectedVideoInput = extractDeviceId({ device: videoInputsContext.selectedDevice }) ?? undefined
  const selectedAudioOutput = audioOutputsContext.selectedDevice ?? undefined

  // Manual selection for an audio input (e.g. microphone)
  const selectAudioInput = useCallback(
    async ({ id }: { id: string }) => {
      try {
        assertMeetingReady()
        await meetingManager.startAudioInputDevice(id)
        setAudioError(undefined)
      } catch (err) {
        setAudioError(formatDeviceError({ error: err, deviceType: 'microphone', operation: 'select' }))
      }
    },
    [assertMeetingReady, meetingManager]
  )

  // Manual selection for an audio output (e.g. speaker)
  const selectAudioOutput = useCallback(
    async ({ id }: { id: string }) => {
      try {
        assertMeetingReady()
        await meetingManager.startAudioOutputDevice(id)
      } catch (err) {
        setAudioError(formatDeviceError({ error: err, deviceType: 'speaker', operation: 'select' }))
      }
    },
    [assertMeetingReady, meetingManager]
  )

  // Manual selection for a video input (e.g. camera)
  const selectVideoInput = useCallback(
    async ({ id }: { id: string }) => {
      try {
        assertMeetingReady()
        await meetingManager.startVideoInputDevice(id)
        setVideoError(undefined)
      } catch (err) {
        setVideoError(formatDeviceError({ error: err, deviceType: 'camera', operation: 'select' }))
      }
    },
    [assertMeetingReady, meetingManager]
  )

  // Refresh the device list
  const refreshDevices = useCallback(async () => {
    if (!isActiveMeeting()) {
      return
    }
    setIsRefreshing(true)
    try {
      assertMeetingReady()
      await meetingManager.listAndSelectDevices(DeviceLabels.AudioAndVideo)
    } catch (err) {
      const errorMsg = formatDeviceError({
        error: err,
        deviceType: 'microphone',
        operation: 'refresh'
      })
      setAudioError(errorMsg)
      setVideoError(errorMsg)
    } finally {
      setIsRefreshing(false)
    }
  }, [assertMeetingReady, isActiveMeeting, meetingManager])

  // Auto-select the first available devices
  useEffect(() => {
    if (!autoSelect || !isActiveMeeting()) return

    const autoSelectDevices = async () => {
      // Auto-select first audio input if none selected
      if (audioInputsContext.devices.length > 0 && !selectedAudioInput) {
        const fallback = audioInputsContext.devices[0]
        try {
          assertMeetingReady()
          await meetingManager.startAudioInputDevice(fallback.deviceId)
        } catch (err) {
          setAudioError(formatDeviceError({ error: err, deviceType: 'microphone', operation: 'select' }))
        }
      }

      // Auto-select first video input if none selected
      if (videoInputsContext.devices.length > 0 && !selectedVideoInput) {
        const fallback = videoInputsContext.devices[0]
        try {
          assertMeetingReady()
          await meetingManager.startVideoInputDevice(fallback.deviceId)
        } catch (err) {
          setVideoError(formatDeviceError({ error: err, deviceType: 'camera', operation: 'select' }))
        }
      }
    }

    void autoSelectDevices()
  }, [
    autoSelect,
    assertMeetingReady,
    audioInputsContext.devices,
    videoInputsContext.devices,
    selectedAudioInput,
    selectedVideoInput,
    meetingManager
  ])

  return {
    audioInputs,
    audioOutputs,
    videoInputs,
    selectedAudioInput,
    selectedAudioOutput,
    selectedVideoInput,
    audioError,
    videoError,
    isRefreshing,
    selectAudioInput,
    selectAudioOutput,
    selectVideoInput,
    refreshDevices
  }
}
